// Panel admin: preguntas entrantes de compradores en publicaciones ML
// Lee de ml_preguntas. Prioridad: unanswered arriba con timer visual (ML penaliza si >2hs).
// Botones: "✨ Sugerencia IA" (Claude Haiku), "Responder" (edge function).

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

export default function PreguntasML() {
  const [preguntas, setPreguntas] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState({ status: 'unanswered' })
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [respondiendo, setRespondiendo] = useState({}) // { question_id: true }
  const [drafts, setDrafts] = useState({}) // { question_id: 'texto' }

  async function cargar() {
    const { data } = await supabase
      .from('ml_preguntas')
      .select('id, ml_question_id, ml_item_id, producto_id, from_nickname, from_name, question_text, answer_text, answered_at, ia_suggested_answer, ia_used, status, date_created')
      .order('date_created', { ascending: false })
      .limit(200)
    setPreguntas(data || [])
    setLoading(false)
  }

  useEffect(() => {
    cargar()
    if (!autoRefresh) return
    const t = setInterval(cargar, 30000)
    return () => clearInterval(t)
  }, [autoRefresh])

  async function pedirSugerenciaIA(pregunta) {
    setRespondiendo({ ...respondiendo, [pregunta.ml_question_id]: 'ia' })
    try {
      // Traer info del producto para dar contexto a la IA
      let productoInfo = null
      if (pregunta.producto_id) {
        const { data } = await supabase.from('productos')
          .select('nombre, marca, presentacion, precio_publico, descripcion, aroma, rendimiento, ideal_para, beneficios, modo_uso, composicion')
          .eq('id', pregunta.producto_id).maybeSingle()
        productoInfo = data
      }

      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-responder-ia`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ml_question_id: pregunta.ml_question_id,
        }),
      })
      const data = await resp.json()
      const sugerida = data?.respuesta_sugerida ?? data?.texto
      if (sugerida) {
        setDrafts({ ...drafts, [pregunta.ml_question_id]: sugerida })
        if (data.decision === 'escala') {
          console.info(`IA sugiere ESCALAR (${data.etiqueta}, confianza ${data.confianza})`)
        }
      } else {
        alert('IA no devolvió respuesta: ' + (data?.error || 'error'))
      }
    } catch (e) {
      alert('Error IA: ' + e.message)
    } finally {
      const { [pregunta.ml_question_id]: _, ...rest } = respondiendo
      setRespondiendo(rest)
    }
  }

  async function responder(pregunta, texto) {
    if (!texto?.trim()) return alert('Escribí la respuesta primero')
    if (!confirm(`Enviar respuesta a ML?\n\n"${texto}"`)) return
    setRespondiendo({ ...respondiendo, [pregunta.ml_question_id]: 'ml' })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ml-responder-pregunta`
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ml_question_id: pregunta.ml_question_id,
          text: texto.trim(),
          uso_ia: drafts[pregunta.ml_question_id] === texto,
        }),
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data?.error || 'error')
      alert('✓ Respuesta enviada a ML')
      const { [pregunta.ml_question_id]: _, ...rest } = drafts
      setDrafts(rest)
      cargar()
    } catch (e) {
      alert('Error: ' + e.message)
    } finally {
      const { [pregunta.ml_question_id]: _, ...rest } = respondiendo
      setRespondiendo(rest)
    }
  }

  const filtradas = useMemo(() => {
    return preguntas.filter(p => {
      if (filtro.status === 'unanswered' && p.status !== 'UNANSWERED' && p.answer_text) return false
      if (filtro.status === 'answered' && (p.status === 'UNANSWERED' || !p.answer_text)) return false
      return true
    })
  }, [preguntas, filtro])

  const stats = useMemo(() => {
    const sinResp = preguntas.filter(p => p.status === 'UNANSWERED' || !p.answer_text)
    const urgentes = sinResp.filter(p => {
      if (!p.date_created) return false
      const hs = (Date.now() - new Date(p.date_created).getTime()) / 3600000
      return hs > 2
    })
    return { total: preguntas.length, sinResp: sinResp.length, urgentes: urgentes.length }
  }, [preguntas])

  function horasHace(fecha) {
    if (!fecha) return null
    const hs = (Date.now() - new Date(fecha).getTime()) / 3600000
    if (hs < 1) return `${Math.round(hs * 60)}min`
    if (hs < 24) return `${Math.round(hs)}h`
    return `${Math.round(hs / 24)}d`
  }

  return (
    <AdminLayout>
      <div className="page">
        <Link to="/admin/canales/mercadolibre" style={{ fontSize: 13, color: 'var(--muted)' }}>← Volver a MercadoLibre</Link>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
          <div>
            <h1 style={{ margin: 0 }}>❓ Preguntas MercadoLibre</h1>
            <p className="lead" style={{ margin: '4px 0 0' }}>
              Preguntas de compradores en publicaciones. <strong>ML penaliza reputación si tardás &gt;2hs.</strong>
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh 30s
          </label>
        </div>

        {/* KPIs */}
        <div className="cards-grid" style={{ marginTop: 16 }}>
          <div className="card"><h3>Total</h3><div className="stat">{stats.total}</div></div>
          <div className="card" style={{ borderLeft: '4px solid #F59E0B' }}><h3>Sin responder</h3><div className="stat" style={{ color: '#F59E0B' }}>{stats.sinResp}</div></div>
          {stats.urgentes > 0 && (
            <div className="card" style={{ borderLeft: '4px solid #EF4444' }}>
              <h3>⚠ Urgentes ({'>'}2hs)</h3>
              <div className="stat" style={{ color: '#EF4444' }}>{stats.urgentes}</div>
              <div className="desc">Impactan reputación ML</div>
            </div>
          )}
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          {[
            { v: 'unanswered', label: 'Sin responder' },
            { v: 'answered', label: 'Respondidas' },
            { v: 'todos', label: 'Todas' },
          ].map(o => (
            <button
              key={o.v}
              onClick={() => setFiltro({ status: o.v })}
              style={{
                padding: '6px 12px', border: '1px solid #CBD5E1',
                background: filtro.status === o.v ? '#1A3461' : '#fff',
                color: filtro.status === o.v ? '#fff' : '#475569',
                borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600,
              }}
            >{o.label}</button>
          ))}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="card" style={{ marginTop: 16, color: 'var(--muted)' }}>Cargando…</div>
        ) : filtradas.length === 0 ? (
          <div className="card" style={{ marginTop: 16, color: 'var(--muted)', textAlign: 'center', padding: 32 }}>
            <div style={{ fontSize: 40, opacity: 0.4 }}>❓</div>
            <p style={{ margin: '10px 0 4px' }}>Sin preguntas con esos filtros.</p>
          </div>
        ) : (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtradas.map(p => {
              const sinResp = !p.answer_text
              const hs = p.date_created ? (Date.now() - new Date(p.date_created).getTime()) / 3600000 : 0
              const urgente = sinResp && hs > 2
              const drafted = drafts[p.ml_question_id]
              const cargando = respondiendo[p.ml_question_id]
              return (
                <div key={p.id} style={{
                  background: '#fff', border: `1px solid ${urgente ? '#FCA5A5' : sinResp ? '#FCD34D' : '#E2E8F0'}`,
                  borderLeft: `4px solid ${urgente ? '#EF4444' : sinResp ? '#F59E0B' : '#10B981'}`,
                  borderRadius: 10, padding: 14,
                }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 12, color: '#1A3461' }}>@{p.from_nickname || 'anónimo'}</strong>
                      {p.ml_item_id && <code style={{ fontSize: 10, background: '#F8FAFC', padding: '1px 5px', borderRadius: 3, color: '#475569' }}>{p.ml_item_id}</code>}
                      {sinResp && <span style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
                        background: urgente ? '#FEE2E2' : '#FEF3C7', color: urgente ? '#7F1D1D' : '#92400E',
                      }}>
                        {urgente ? '⚠ URGENTE · ' : ''}hace {horasHace(p.date_created)}
                      </span>}
                    </div>
                    {p.producto_id && (
                      <Link to={`/admin/stock/${p.producto_id}`} style={{ fontSize: 11, color: '#3B82F6', textDecoration: 'none', fontWeight: 700 }}>
                        → Ficha producto
                      </Link>
                    )}
                  </div>

                  {/* Pregunta */}
                  <div style={{ padding: 10, background: '#F8FAFC', borderRadius: 6, fontSize: 13, color: '#1A3461', lineHeight: 1.5, marginBottom: 8 }}>
                    <strong style={{ fontSize: 10, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Pregunta</strong>
                    {p.question_text}
                  </div>

                  {/* Si ya respondió */}
                  {p.answer_text && (
                    <div style={{ padding: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 6, fontSize: 13, color: '#065F46', lineHeight: 1.5, marginBottom: 8 }}>
                      <strong style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 4 }}>Tu respuesta · {horasHace(p.answered_at)} después</strong>
                      {p.answer_text}
                    </div>
                  )}

                  {/* Editor respuesta (solo si sin responder) */}
                  {sinResp && (
                    <>
                      <textarea
                        value={drafted ?? p.ia_suggested_answer ?? ''}
                        onChange={e => setDrafts({ ...drafts, [p.ml_question_id]: e.target.value })}
                        placeholder="Escribí tu respuesta o pedí sugerencia IA con el botón ✨"
                        rows={3}
                        style={{
                          width: '100%', padding: '8px 10px',
                          border: '1px solid #CBD5E1', borderRadius: 6,
                          fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => pedirSugerenciaIA(p)}
                          disabled={!!cargando}
                          style={{
                            background: '#7C3AED', color: '#fff', border: 'none',
                            padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                            cursor: cargando ? 'wait' : 'pointer', opacity: cargando ? 0.6 : 1,
                          }}
                        >
                          {cargando === 'ia' ? '✨ Generando…' : '✨ Sugerencia IA'}
                        </button>
                        <button
                          onClick={() => responder(p, drafted ?? p.ia_suggested_answer ?? '')}
                          disabled={!!cargando || !(drafted ?? p.ia_suggested_answer)}
                          style={{
                            background: '#3B82F6', color: '#fff', border: 'none',
                            padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                            cursor: cargando ? 'wait' : 'pointer', opacity: cargando ? 0.6 : 1,
                          }}
                        >
                          {cargando === 'ml' ? 'Enviando…' : 'Responder →'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
